import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  ArrowRightIcon, ClockIcon,
  CheckCircleIcon, CalendarIcon,
  ChatBubbleLeftRightIcon,
  FireIcon, MusicalNoteIcon, ArrowPathIcon, TrophyIcon,
  HeartIcon, StarIcon, BoltIcon,
} from '@heroicons/react/24/outline';

// Import local image for hero and cards
import studioImage from '../../assets/images/pcf studio out.png';

// ─── FONT IMPORT ──────────────────────────────────────────────────────────────
const FONTS = `@import url('https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@700;900&family=DM+Sans:wght@300;400;500;600&display=swap');`;

// ─── WHATSAPP NUMBER ─────────────────────────────────────────────────────────
const WA_NUMBER = '254117110000';

// ─── EXACTLY 7 CARDS - Each card shows BOTH AM & PM sessions ─────────────────
const ACTIVITIES = [
  // MONDAY - Morning & Evening
  {
    id: 'monday',
    title: 'STRENGTH DAY',
    subtitle: 'STRENGTH & CONDITIONING',
    day: 'MONDAY',
    icon: FireIcon,
    accentColor: '#e63946',
    sessions: [
      { period: 'Morning', time: '6:00 – 7:00 AM', coaches: 'COACH JEMOH' },
      { period: 'Evening', time: '6:00 – 7:00 PM', coaches: 'COACH SAM' },
    ],
  },
  // TUESDAY - Morning & Evening
  {
    id: 'tuesday',
    title: 'ENDURANCE & CONDITIONING',
    subtitle: 'COMBO-GRIT (AM) / URBAN HIIT (PM)',
    day: 'TUESDAY',
    icon: BoltIcon,
    accentColor: '#f4a261',
    sessions: [
      { period: 'Morning', time: '6:00 – 7:00 AM', coaches: 'COACH DENIS' },
      { period: 'Evening', time: '6:00 – 7:00 PM', coaches: 'COACH MUCHIRI' },
    ],
  },
  // WEDNESDAY - Morning & Evening
  {
    id: 'wednesday',
    title: 'SPIN ENERGY',
    subtitle: 'AFROBEAT (AM) / CYCLE X (PM)',
    day: 'WEDNESDAY',
    icon: ArrowPathIcon,
    accentColor: '#2a9d8f',
    sessions: [
      { period: 'Morning', time: '6:00 – 7:00 AM', coaches: 'COACH BERNICE' },
      { period: 'Evening', time: '6:00 – 7:00 PM', coaches: 'COACH ZACK' },
    ],
  },
  // THURSDAY - Morning & Evening
  {
    id: 'thursday',
    title: 'PERFORMANCE & VIBES',
    subtitle: 'HYROX (AM) / FLOW STEPS (PM)',
    day: 'THURSDAY',
    icon: TrophyIcon,
    accentColor: '#800020',
    sessions: [
      { period: 'Morning', time: '6:00 – 7:00 AM', coaches: 'COACH NJERY' },
      { period: 'Evening', time: '6:00 – 7:00 PM', coaches: 'COACH ERIC' },
    ],
  },
  // FRIDAY - Morning & Evening
  {
    id: 'friday',
    title: 'INTENSITY FINISH',
    subtitle: 'URBAN HIIT (AM) / AFROBEAT (PM)',
    day: 'FRIDAY',
    icon: HeartIcon,
    accentColor: '#6a0572',
    sessions: [
      { period: 'Morning', time: '6:00 – 7:00 AM', coaches: 'COACH SIMON' },
      { period: 'Evening', time: '6:00 – 7:00 PM', coaches: 'COACH BERNICE' },
    ],
  },
  // SATURDAY - Morning ONLY
  {
    id: 'saturday',
    title: 'ELITE SESSION',
    subtitle: 'LEGENDS BOOTCAMP',
    day: 'SATURDAY',
    icon: StarIcon,
    accentColor: '#0077b6',
    sessions: [
      { period: 'Morning', time: '6:00 – 7:00 AM', coaches: 'COACH DENIS, SAM, MUCHIRI & JEMOH' },
    ],
  },
  // END MONTH - Special Event
  {
    id: 'end-month',
    title: 'END MONTH',
    subtitle: 'BODY ATTACK CHALLENGE',
    day: 'SPECIAL EVENT',
    icon: TrophyIcon,
    accentColor: '#9b2d40',
    sessions: [
      { period: 'Special', time: '6:00 AM – 7:30 AM', coaches: 'ALL COACHES' },
    ],
  },
];
// ─── HELPERS ─────────────────────────────────────────────────────────────────
const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  transition: { duration: 0.6, delay },
  viewport: { once: true },
});

const buildWhatsAppMessage = (activity) => {
  const sessionDetails = activity.sessions.map(s => `${s.period}: ${s.time} (${s.coaches})`).join('\n');
  const msg = `Hi PowerCore! 👋\n\nI'm interested in the *${activity.title}* class.\n\n📅 Day: ${activity.day}\n📋 Class: ${activity.subtitle}\n\n🕐 Sessions:\n${sessionDetails}\n\nPlease let me know how I can book a spot. Thank you!`;
  return `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(msg)}`;
};

// ─── ACTIVITY CARD - Shows BOTH sessions in one card ─────────────────────────
const ActivityCard = ({ activity, index }) => {
  const Icon = activity.icon;
  const whatsappUrl = buildWhatsAppMessage(activity);

  return (
    <motion.div
      initial={{ opacity: 0, y: 32 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: (index % 4) * 0.06 }}
      viewport={{ once: true }}
      className="group rounded-2xl overflow-hidden bg-white shadow-md hover:shadow-xl transition-all duration-500 hover:-translate-y-1.5 flex flex-col h-full">
      {/* Image */}
      <div className="relative overflow-hidden flex-shrink-0" style={{ height: 140 }}>
        <img 
          src={studioImage} 
          alt={activity.title}
          className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700" 
        />
        <div className="absolute top-3 left-3 w-9 h-9 rounded-xl flex items-center justify-center shadow-md"
          style={{ background: activity.accentColor }}>
          <Icon className="h-4 w-4 text-white" />
        </div>
        {/* Day Badge */}
        <div className="absolute top-3 right-3 px-2 py-1 rounded-lg text-white font-bold text-xs"
          style={{ background: activity.accentColor }}>
          {activity.day}
        </div>
      </div>
      
      {/* Content */}
      <div className="p-4 flex flex-col flex-1">
        <span className="inline-block text-[10px] font-bold px-2 py-0.5 rounded-full mb-2 self-start"
          style={{ background: activity.accentColor + '15', color: activity.accentColor }}>
          {activity.subtitle}
        </span>
        <h3 className="font-black text-gray-900 mb-3 leading-tight"
          style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: '1.3rem' }}>
          {activity.title}
        </h3>
        
        {/* Sessions - Show both AM & PM */}
        <div className="space-y-2 mb-3">
          {activity.sessions.map((session, idx) => (
            <div key={idx} className="flex flex-col gap-0.5 p-2 rounded-lg" style={{ background: '#f8f8f8' }}>
              <div className="flex items-center gap-1.5">
                <ClockIcon className="h-3 w-3" style={{ color: activity.accentColor }} />
                <span className="text-gray-700 text-xs font-semibold">{session.time}</span>
                <span className="text-[10px] px-1.5 py-0.5 rounded-full ml-auto" 
                  style={{ background: activity.accentColor + '20', color: activity.accentColor }}>
                  {session.period}
                </span>
              </div>
              <p className="text-gray-500 text-[10px] ml-4">
                👨‍🏫 {session.coaches}
              </p>
            </div>
          ))}
        </div>
        
        {/* WhatsApp Enquire Button */}
        <a
          href={whatsappUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="w-full inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl text-xs font-bold text-white transition-all hover:scale-[1.02] mt-auto"
          style={{ background: '#25D366' }}>
          <ChatBubbleLeftRightIcon className="h-3.5 w-3.5" />
          Enquire on WhatsApp
        </a>
      </div>
    </motion.div>
  );
};

// ─── INFO PANEL (unchanged) ────────────────────────────────────────────────────
const InfoPanel = () => (
  <div className="grid gap-5">
    {/* Class Schedule Summary */}
    <div className="rounded-2xl overflow-hidden shadow-sm" style={{ background: '#1a1a2e' }}>
      <div className="px-5 pt-5 pb-4">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: '#800020' }}>
            <CalendarIcon className="h-4 w-4 text-white" />
          </div>
          <h3 className="font-black text-white text-sm uppercase tracking-wider" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
            Weekly Schedule
          </h3>
        </div>
        <div className="space-y-3">
          <div className="flex flex-col gap-1 border-b border-white/10 pb-2">
            <span className="text-white/60 text-xs font-medium">Monday – Friday</span>
            <div className="flex flex-wrap gap-2">
              <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: '#e63946', color: 'white' }}>🌅 6:00 AM – 7:00 AM</span>
              <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: '#1e88e5', color: 'white' }}>🌙 6:00 PM – 7:00 PM</span>
            </div>
          </div>
          <div className="flex flex-col gap-1 border-b border-white/10 pb-2">
            <span className="text-white/60 text-xs font-medium">Saturday</span>
            <span className="text-xs font-bold px-2 py-0.5 rounded-full inline-block" style={{ background: '#0077b6', color: 'white' }}>🌅 6:00 AM – 7:00 AM</span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-white/60 text-xs font-medium">End Month Special</span>
            <span className="text-xs font-bold px-2 py-0.5 rounded-full inline-block" style={{ background: '#9b2d40', color: 'white' }}>⭐ 6:00 AM – 7:30 AM</span>
          </div>
        </div>
      </div>
    </div>

    {/* Opening Hours */}
    <div className="rounded-2xl overflow-hidden shadow-sm" style={{ background: '#0d1b2a' }}>
      <div className="px-5 pt-5 pb-4">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: '#f4a261' }}>
            <ClockIcon className="h-4 w-4 text-white" />
          </div>
          <h3 className="font-black text-white text-sm uppercase tracking-wider" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
            Opening Hours
          </h3>
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between border-b border-white/10 pb-2">
            <span className="text-white/60 text-xs">Monday – Friday</span>
            <span className="text-white text-xs font-bold">4:00 AM – 10:00 PM</span>
          </div>
          <div className="flex items-center justify-between border-b border-white/10 pb-2">
            <span className="text-white/60 text-xs">Saturday</span>
            <span className="text-white text-xs font-bold">6:00 AM – 9:00 PM</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-white/60 text-xs">Sunday & Holidays</span>
            <span className="text-white text-xs font-bold">8:00 AM – 8:00 PM</span>
          </div>
        </div>
      </div>
    </div>

    {/* What You Get */}
    <div className="rounded-2xl overflow-hidden shadow-sm" style={{ background: '#800020' }}>
      <div className="px-5 pt-5 pb-4">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center">
            <StarIcon className="h-4 w-4 text-white" />
          </div>
          <h3 className="font-black text-white text-sm uppercase tracking-wider" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
            What You Get
          </h3>
        </div>
        <div className="space-y-2">
          {[
            'General & Personal Training',
            'Free Recovery Zone (Sauna)',
            'Two Hours Free Parking',
            'Free WiFi',
            'Secure Lockers',
            'Laundry Services (small fee)',
            'Supplements & Accessories Shop',
          ].map((item, i) => (
            <div key={i} className="flex items-center gap-2">
              <CheckCircleIcon className="h-3.5 w-3.5 text-white/70 flex-shrink-0" />
              <span className="text-white/85 text-xs">{item}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
const Activities = () => {
  const gridRef = useRef(null);
  const scrollToGrid = () => gridRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <style>{FONTS}</style>

      {/* HERO SECTION */}
      <section className="relative w-full h-[400px] overflow-hidden">
        <img
          src={studioImage}
          alt="PowerCore Classes" 
          className="absolute inset-0 w-full h-full object-cover object-center" />
        <div className="absolute inset-0"
          style={{ background: 'linear-gradient(120deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.4) 55%, rgba(0,0,0,0.3) 100%)' }} />
        <div className="absolute inset-0 flex flex-col justify-center px-6 sm:px-12">
          <motion.div {...fade(0.1)} className="max-w-2xl">
            <span className="inline-block text-xs font-bold tracking-[0.25em] uppercase px-3 py-1 rounded-full mb-4 border border-white/30 text-white/70">
              PowerCore Classes
            </span>
            <h1 className="font-black text-white mb-3 leading-none"
              style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 'clamp(2rem,5vw,3.8rem)', letterSpacing: '-0.02em' }}>
              TRAIN HARD.<br />
              <span style={{ color: '#e8a0b0' }}>RECOVER SMART.</span>
            </h1>
            <p className="text-white/80 text-sm leading-relaxed max-w-lg">
              Morning and evening sessions available Monday through Friday. 
              Find the class that fits your schedule and fire up your fitness journey.
            </p>
            <div className="flex flex-wrap gap-3 mt-6">
              <button onClick={scrollToGrid}
                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full font-bold text-sm text-white transition-all hover:scale-105"
                style={{ background: '#800020', boxShadow: '0 4px 20px rgba(128,0,32,0.4)' }}>
                View Schedule <ArrowRightIcon className="h-4 w-4" />
              </button>
            </div>
          </motion.div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-16" style={{ background: 'linear-gradient(to top, #faf8f8, transparent)' }} />
      </section>

      {/* CLASSES GRID SECTION - 7 CARDS TOTAL */}
      <section ref={gridRef} className="py-14" style={{ background: '#faf8f8' }}>
        <div className="container mx-auto px-4 max-w-7xl">
          <motion.div {...fade(0)} className="mb-10 text-center">
            <span className="text-xs font-bold tracking-[0.2em] uppercase mb-2 inline-block" style={{ color: '#800020' }}>
              Weekly Schedule
            </span>
            <h2 className="font-black text-gray-900"
              style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 'clamp(1.8rem,3.5vw,2.8rem)' }}>
              TRAIN WITH THE BEST
            </h2>
            <p className="text-gray-500 text-sm max-w-md mx-auto mt-2">
              🌅 Morning: 6:00 – 7:00 AM | 🌙 Evening: 6:00 – 7:00 PM (Mon-Fri)
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-3 gap-8 items-start">
            {/* Cards Grid - 7 cards displayed in a single grid */}
            <div className="lg:col-span-2">
              <div className="grid sm:grid-cols-2 gap-5">
                {ACTIVITIES.map((activity, i) => (
                  <ActivityCard
                    key={activity.id}
                    activity={activity}
                    index={i}
                  />
                ))}
              </div>

              {/* Call to Action Banner */}
              <motion.div {...fade(0.1)} className="mt-8 rounded-2xl overflow-hidden"
                style={{ background: 'linear-gradient(135deg,#f9e8ec 0%,#f0bbc6 100%)' }}>
                <div className="flex flex-col sm:flex-row items-center justify-between gap-6 px-6 py-6">
                  <div>
                    <p className="text-xs font-bold tracking-[0.2em] uppercase mb-1" style={{ color: '#800020' }}>Unlimited Access</p>
                    <h3 className="font-black text-gray-900 text-xl" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                      LOVE WHAT YOU SEE? JOIN A PLAN.
                    </h3>
                    <p className="text-gray-600 text-sm mt-1">Membership gives you access to all classes — unlimited, every day.</p>
                  </div>
                  <Link to="/pricing"
                    className="flex-shrink-0 inline-flex items-center gap-2 px-6 py-2.5 rounded-full font-bold text-sm text-white transition-all hover:scale-105"
                    style={{ background: '#800020', boxShadow: '0 4px 20px rgba(128,0,32,0.3)' }}>
                    View Plans <ArrowRightIcon className="h-4 w-4" />
                  </Link>
                </div>
              </motion.div>
            </div>

            {/* Info Panel Sidebar */}
            <motion.div {...fade(0.2)} className="lg:sticky lg:top-8">
              <InfoPanel />
            </motion.div>
          </div>
        </div>
      </section>

      {/* READY TO START SECTION */}
      <section className="py-14 relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg,#800020 0%,#3d0010 100%)' }}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-64 h-64 bg-white rounded-full filter blur-3xl" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full filter blur-3xl" />
        </div>
        <div className="container mx-auto px-4 max-w-7xl text-center relative z-10">
          <motion.div {...fade(0)}>
            <span className="text-xs font-bold tracking-[0.2em] uppercase px-4 py-1.5 rounded-full inline-block mb-4 bg-white/20 text-white/80">
              Limited Spots Available
            </span>
            <h2 className="font-black text-white mb-4 leading-none"
              style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 'clamp(2rem,4vw,3.5rem)' }}>
              READY TO START YOUR <span className="text-yellow-400">JOURNEY?</span>
            </h2>
            <p className="text-white/75 mb-8 max-w-lg mx-auto text-sm leading-relaxed">
              Join PowerCore today and transform your life. No contracts. No excuses. Just results.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/pricing"
                className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full font-black text-sm text-gray-900 bg-white transition-all hover:scale-105 hover:shadow-2xl">
                View Pricing &amp; Join <ArrowRightIcon className="h-4 w-4" />
              </Link>
              <a
                href={`https://wa.me/${WA_NUMBER}?text=${encodeURIComponent("Hi PowerCore! I'd like to know more about your classes and membership options.")}`}
                target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full font-bold text-sm text-white border-2 border-white/40 hover:bg-white/10 transition-all">
                <ChatBubbleLeftRightIcon className="h-4 w-4" />
                Chat on WhatsApp
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Activities;